import React from "react";
import { Button, Card, Space, Badge, Dropdown, Typography, Divider, message, Modal, FloatButton } from "antd";
import { PlusOutlined, DownloadOutlined, MoreOutlined, EditOutlined, TagsOutlined, UserOutlined, FileTextOutlined, EnvironmentOutlined, DatabaseOutlined, DeleteOutlined, ExportOutlined, ImportOutlined } from "@ant-design/icons";
import { UldProvider, useUld } from "./context/UldContext";
import UldForm from "./components/UldForm";
import UldFilter from "./components/UldFilter";
import UldTable from "./components/UldTable";
import HolderBatchUpdateModal from "./components/HolderBatchUpdateModal";
import RemarksBatchUpdateModal from "./components/RemarksBatchUpdateModal";
import LocationBatchUpdateModal from "./components/LocationBatchUpdateModal";
import StatusBatchUpdateModal from "./components/StatusBatchUpdateModal";
import TagsBatchManageModal from "./components/TagsBatchManageModal";
import BatchDeleteModal from "./components/BatchDeleteModal";
import UldBatchDeliveryModal from "./components/UldBatchDeliveryModal";
import { FilteredExportButton } from "../../../components/export";
import PermissionGuard from "../../../components/auth/PermissionGuard";
import { useAuth } from "../../../hooks/useAuth";

const { Title } = Typography;

/**
 * ULD管理 - 主页面内容组件
 * 必须在UldProvider内部使用
 *
 * 架构设计说明：
 *
 * 1. 项目架构遵循分层设计模式：
 *    - context/UldContext：全局状态管理，连接API和UI组件
 *    - components/：纯UI组件，通过useUld hook获取状态和方法
 *
 * 2. 主要数据流向：
 *    - 用户交互 -> 组件调用Context提供的方法 -> Context调用API ->
 *      Context更新状态 -> 组件自动重新渲染
 */
const UldManagementContent = () => {
    const { showCreateForm, totalUlds, uldList, fetchUlds, filters } = useUld();

    // 表格选择状态
    const [selectedUldIds, setSelectedUldIds] = React.useState([]);
    const [selectedRowKeys, setSelectedRowKeys] = React.useState([]);
    const [selectedUlds, setSelectedUlds] = React.useState([]);
    const [clearSelectionFn, setClearSelectionFn] = React.useState(null);
    const [handleRowSelectionChangeFn, setHandleRowSelectionChangeFn] = React.useState(null);

    // 批量操作状态
    const [holderModalVisible, setHolderModalVisible] = React.useState(false);
    const [remarksModalVisible, setRemarksModalVisible] = React.useState(false);
    const [locationModalVisible, setLocationModalVisible] = React.useState(false);
    const [statusModalVisible, setStatusModalVisible] = React.useState(false);
    const [tagManageModalVisible, setTagManageModalVisible] = React.useState(false);
    const [deleteModalVisible, setDeleteModalVisible] = React.useState(false);
    const [batchDeliveryModalVisible, setBatchDeliveryModalVisible] = React.useState(false);
    const [batchDeliveryMode, setBatchDeliveryMode] = React.useState('deliver'); // 'deliver' | 'revoke'

    // 处理表格选择变化
    const handleSelectionChange = React.useCallback((uldIds, selectedRows, rowKeys, clearFn, handleRowSelectionChange) => {
        setSelectedUldIds(uldIds);
        setSelectedRowKeys(rowKeys);
        setSelectedUlds(selectedRows);
        setClearSelectionFn(() => clearFn);
        setHandleRowSelectionChangeFn(() => handleRowSelectionChange);
    }, []);

    // 全选当前页面所有ULD
    const handleSelectAllVisible = React.useCallback(() => {
        if (handleRowSelectionChangeFn) {
            const visibleKeys = uldList.map((item) => item.master.id);
            handleRowSelectionChangeFn(visibleKeys, uldList);
        }
    }, [uldList, handleRowSelectionChangeFn]);

    // 清空选择
    const handleClearSelection = React.useCallback(() => {
        if (clearSelectionFn) {
            clearSelectionFn();
        }
    }, [clearSelectionFn]);

    // 构建导出过滤参数（缓存计算结果，避免每次渲染创建新对象）
    const exportFilterParams = React.useMemo(() => {
        const exportFilters = {};
        
        const directMappingFields = [
            'uld_identifiers', 'delivery_status', 'ghas', 'ownership_types', 
            'tag_names', 'remarks_keyword'
        ];
        
        directMappingFields.forEach(field => {
            if (filters[field] !== undefined && filters[field] !== null) {
                exportFilters[field] = filters[field];
            }
        });

        const fieldMappings = {
            'airport_iatas': 'airport_iata',
            'core_statuses': 'core_status',
            'availabilities': 'availability',
            'uld_types': 'uld_type',
            'owners': 'owner',
            'holders': 'holder'
        };
        
        Object.entries(fieldMappings).forEach(([frontendField, backendField]) => {
            if (filters[frontendField] !== undefined && filters[frontendField] !== null) {
                exportFilters[backendField] = filters[frontendField];
            }
        });

        return exportFilters;
    }, [filters]);

    // 批量操作菜单 - 根据权限动态生成
    const { hasPermission } = useAuth();
    const batchActionsMenuItems = [];

    // 批量更新持有者（需要批量更新权限）
    if (hasPermission('uld:batch_update')) {
        batchActionsMenuItems.push({
            key: "holder-update",
            icon: <UserOutlined />,
            label: "批量更新持有者",
            onClick: () => setHolderModalVisible(true),
            disabled: selectedUldIds.length === 0,
        });
    }

    // 批量更新位置（需要批量更新权限）
    if (hasPermission('uld:batch_update')) {
        batchActionsMenuItems.push({
            key: "location-update",
            icon: <EnvironmentOutlined />,
            label: "批量更新位置",
            onClick: () => setLocationModalVisible(true),
            disabled: selectedUldIds.length === 0,
        });
    }

    // 批量更新备注（需要批量更新权限）
    if (hasPermission('uld:batch_update')) {
        batchActionsMenuItems.push({
            key: "remarks-update",
            icon: <FileTextOutlined />,
            label: "批量更新备注",
            onClick: () => setRemarksModalVisible(true),
            disabled: selectedUldIds.length === 0,
        });
    }

    // 批量状态更新（需要状态更新权限）
    if (hasPermission('uld:status_update')) {
        batchActionsMenuItems.push({
            key: "status-update",
            icon: <EditOutlined />,
            label: "批量状态更新",
            onClick: () => setStatusModalVisible(true),
            disabled: selectedUldIds.length === 0,
        });
    }

    // 批量标签管理（需要标签管理权限）
    if (hasPermission('uld:tag:manage')) {
        batchActionsMenuItems.push({
            key: "tag-manage",
            icon: <TagsOutlined />,
            label: "批量标签管理",
            onClick: () => setTagManageModalVisible(true),
            disabled: selectedUldIds.length === 0,
        });
    }

    // 批量外放管理（需要批量更新权限）
    if (hasPermission('uld:batch_update')) {
        // 添加分隔线
        if (batchActionsMenuItems.length > 0) {
            batchActionsMenuItems.push({ type: "divider" });
        }

        batchActionsMenuItems.push({
            key: "batch-deliver",
            icon: <ExportOutlined />,
            label: "批量外放",
            onClick: () => {
                setBatchDeliveryMode('deliver');
                setBatchDeliveryModalVisible(true);
            },
            disabled: selectedUldIds.length === 0,
        });

        batchActionsMenuItems.push({
            key: "batch-revoke-delivery",
            icon: <ImportOutlined />,
            label: "批量撤销外放",
            onClick: () => {
                setBatchDeliveryMode('revoke');
                setBatchDeliveryModalVisible(true);
            },
            disabled: selectedUldIds.length === 0,
        });
    }

    // 分隔线和删除操作
    if (hasPermission('uld:delete')) {
        if (batchActionsMenuItems.length > 0) {
            batchActionsMenuItems.push({ type: "divider" });
        }
        batchActionsMenuItems.push({
            key: "batch-delete",
            icon: <DeleteOutlined />,
            label: "批量删除",
            onClick: () => setDeleteModalVisible(true),
            disabled: selectedUldIds.length === 0,
            danger: true,
        });
    }

    const batchActionsMenu = {
        items: batchActionsMenuItems,
    };

    return (
        <div className="page-container" style={{ padding: "12px", height: "100vh", display: "flex", flexDirection: "column" }}>
            {/* 页面标题区域 - 紧凑布局 */}
            <div style={{ marginBottom: "8px", flexShrink: 0 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div style={{ display: "flex", alignItems: "center" }}>
                        <Title level={3} style={{ margin: 0, display: "flex", alignItems: "center" }}>
                            <DatabaseOutlined style={{ marginRight: "8px", color: "#1890ff" }} />
                            ULD综合管理
                            <Badge
                                count={totalUlds}
                                style={{
                                    backgroundColor: "#1890ff",
                                    marginLeft: 8,
                                }}
                                overflowCount={9999}
                            />
                        </Title>
                        <span style={{ marginLeft: "16px", color: "rgba(0, 0, 0, 0.45)", fontSize: "13px" }}>ULD资产全生命周期管理</span>
                    </div>
                    <Space>
                        <PermissionGuard permission="uld:export">
                            <FilteredExportButton
                                resourceType="uld"
                                filterParams={exportFilterParams}
                                buttonText="导出数据"
                                buttonProps={{
                                    icon: <DownloadOutlined />,
                                    size: "small",
                                }}
                            />
                        </PermissionGuard>
                        <PermissionGuard permission="uld:create">
                            <Button type="primary" icon={<PlusOutlined />} onClick={showCreateForm} size="small">
                                创建ULD
                            </Button>
                        </PermissionGuard>
                    </Space>
                </div>
            </div>

            {/* 筛选区域 - 紧凑布局 */}
            <Card className="filter-card" size="small" style={{ marginBottom: "6px", flexShrink: 0 }} styles={{ body: { padding: "12px" } }}>
                <UldFilter />
            </Card>

            {/* 批量操作工具栏 */}
            {selectedUldIds?.length > 0 && (
                <PermissionGuard
                    permission={["uld:batch_update", "uld:status_update", "uld:tag:manage", "uld:delete"]}
                    requireAll={false}
                >
                    <Card
                        className="batch-operations-toolbar"
                        size="small"
                        style={{
                            marginBottom: "6px",
                            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                            border: "none",
                            flexShrink: 0,
                        }}
                        styles={{ body: { padding: "8px 12px" } }}
                    >
                        <div
                            style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                color: "white",
                            }}
                        >
                            <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", width: "60%", pointerEvents: "auto" }}>
                                <span style={{ marginRight: 8, fontSize: "12px" }}>
                                    已选择 <span style={{ fontWeight: "bold" }}>{selectedUldIds.length}</span> 个ULD
                                </span>
                                <Divider type="vertical" style={{ margin: "0 4px", borderColor: "rgba(255,255,255,0.3)" }} />
                                <Button size="small" type="link" onClick={handleSelectAllVisible} style={{ padding: "0 4px", color: "rgba(255,255,255,0.9)" }}>
                                    全选当前页
                                </Button>
                                <Divider type="vertical" style={{ margin: "0 4px", borderColor: "rgba(255,255,255,0.3)" }} />
                                <Button size="small" type="link" onClick={handleClearSelection} style={{ padding: "0 4px", color: "rgba(255,255,255,0.9)" }}>
                                    取消选择
                                </Button>
                            </div>
                            <Space>
                                {batchActionsMenuItems.length > 0 && (
                                    <Dropdown menu={batchActionsMenu} trigger={["click"]}>
                                        <Button
                                            size="small"
                                            icon={<MoreOutlined />}
                                            style={{
                                                background: "rgba(255,255,255,0.2)",
                                                border: "1px solid rgba(255,255,255,0.3)",
                                                color: "white",
                                            }}
                                        >
                                            批量操作
                                        </Button>
                                    </Dropdown>
                                )}
                            </Space>
                        </div>
                    </Card>
                </PermissionGuard>
            )}

            {/* 数据表格区域 - 占据剩余空间 */}
            <div style={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }}>
                {/* 数据统计信息 - 紧凑显示 */}
                <div
                    style={{
                        marginBottom: "4px",
                        fontSize: "12px",
                        color: "rgba(0, 0, 0, 0.65)",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        padding: "0 4px",
                    }}
                >
                    <span>共 {totalUlds} 条记录</span>
                    {selectedUldIds?.length > 0 && <span style={{ color: "#1890ff" }}>已选择 {selectedUldIds.length} 项</span>}
                </div>

                {/* 表格容器 */}
                <Card
                    className="data-card"
                    size="small"
                    style={{ flex: 1, display: "flex", flexDirection: "column" }}
                    styles={{ body: {
                        padding: "8px",
                        flex: 1,
                        display: "flex",
                        flexDirection: "column",
                        overflow: "hidden",
                    } }}
                >
                    <div style={{ flex: 1, overflow: "hidden" }}>
                        <UldTable onSelectionChange={handleSelectionChange} />
                    </div>
                </Card>
            </div>

            {/* ULD创建/编辑表单 */}
            <UldForm />

            {/* 批量更新持有者弹窗 */}
            <HolderBatchUpdateModal
                visible={holderModalVisible}
                onCancel={() => setHolderModalVisible(false)}
                onSuccess={() => {
                    setHolderModalVisible(false);
                    handleClearSelection();
                    fetchUlds();
                }}
                selectedUlds={selectedUlds}
            />

            {/* 批量更新备注弹窗 */}
            <RemarksBatchUpdateModal
                visible={remarksModalVisible}
                onCancel={() => setRemarksModalVisible(false)}
                onSuccess={() => {
                    setRemarksModalVisible(false);
                    handleClearSelection();
                    fetchUlds();
                }}
                selectedUlds={selectedUlds}
            />

            {/* 批量更新位置弹窗：跨页勾选以 selectedUlds 为准，不要再按当前页列表裁剪 */}
            <LocationBatchUpdateModal
                visible={locationModalVisible}
                onCancel={() => setLocationModalVisible(false)}
                onSuccess={() => {
                    setLocationModalVisible(false);
                    handleClearSelection();
                    fetchUlds();
                }}
                selectedUlds={selectedUlds}
            />

            {/* 批量状态更新弹窗 */}
            <StatusBatchUpdateModal
                visible={statusModalVisible}
                onCancel={() => setStatusModalVisible(false)}
                onSuccess={() => {
                    setStatusModalVisible(false);
                    handleClearSelection();
                    fetchUlds();
                }}
                selectedUlds={selectedUlds}
            />

            {/* 批量标签管理弹窗 */}
            <TagsBatchManageModal
                visible={tagManageModalVisible}
                onCancel={() => setTagManageModalVisible(false)}
                onSuccess={() => {
                    setTagManageModalVisible(false);
                    handleClearSelection();
                    fetchUlds();
                }}
                selectedUlds={selectedUlds}
            />

            {/* 批量外放管理弹窗 */}
            <UldBatchDeliveryModal
                visible={batchDeliveryModalVisible}
                onCancel={() => setBatchDeliveryModalVisible(false)}
                onConfirm={async (data) => {
                    const { uldDeliveryService } = await import("../../../api/enhancedApi");
                    try {
                        let result;
                        if (batchDeliveryMode === 'deliver') {
                            result = await uldDeliveryService.batchDeliverToConsignee(data);
                        } else {
                            result = await uldDeliveryService.batchRevokeDelivery(data);
                        }
                        
                        if (result.data.success) {
                            message.success(result.data.message);
                            setBatchDeliveryModalVisible(false);
                            handleClearSelection();
                            await fetchUlds();
                            return true;
                        } else {
                            const failureData = result.data.data;
                            Modal.error({
                                title: '批量操作失败',
                                content: (
                                    <div>
                                        <p>{result.data.message}</p>
                                        {failureData && failureData.failure_items && failureData.failure_items.length > 0 && (
                                            <div style={{ marginTop: 12 }}>
                                                <strong>失败详情：</strong>
                                                <ul style={{ marginTop: 8, paddingLeft: 20 }}>
                                                    {failureData.failure_items.map((item, index) => (
                                                        <li key={index}>
                                                            ULD ID {item.uld_id}: {item.error}
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                        )}
                                    </div>
                                ),
                                width: 500
                            });
                            return false;
                        }
                    } catch (error) {
                        throw error;
                    }
                }}
                selectedUlds={selectedUlds}
                mode={batchDeliveryMode}
            />

            {/* 批量删除弹窗 */}
            <BatchDeleteModal
                visible={deleteModalVisible}
                onCancel={() => setDeleteModalVisible(false)}
                onSuccess={() => {
                    setDeleteModalVisible(false);
                    handleClearSelection();
                    fetchUlds();
                }}
                selectedUlds={selectedUlds}
            />

            <FloatButton.BackTop />
        </div>
    );
};

/**
 * ULD管理 - 主页面
 * 包含UldProvider以提供上下文管理
 */
const UldManagementPage = () => {
    return (
        <UldProvider>
            <UldManagementContent />
        </UldProvider>
    );
};

export default UldManagementPage;
